import React from "react";
import { ExhibitionCountry } from "@/hooks/useExhibition";
import { MapPin, Flame, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import FavoriteButton from "./FavoriteButton";

interface ExhibitionCountryCardProps {
  country: ExhibitionCountry;
  isFavorite?: boolean;
  onClick: () => void;
  onToggleFavorite?: () => void;
}

const ExhibitionCountryCard: React.FC<ExhibitionCountryCardProps> = ({ 
  country, 
  isFavorite = false,
  onClick, 
  onToggleFavorite,
}) => {
  // Compute country status - all countries are accessible now
  const isActive = country.participantCount > 0 || country.boothCount > 0 || country.cityCount > 0;
  const isHot = country.boothCount > 5 || country.participantCount > 20;

  return (
    <button
      onClick={onClick}
      className={cn(
        "w-full p-4 rounded-2xl backdrop-blur-xl bg-white/50 border border-white/60 shadow-lg transition-all duration-300 hover:scale-[1.02] hover:shadow-xl text-left",
        isActive && "ring-2 ring-pop/30",
        isHot && "ring-2 ring-amber-400/50",
        isFavorite && "ring-2 ring-amber-500/50"
      )}
    >
      <div className="flex items-center gap-4">
        {/* Flag */}
        <div className="text-4xl">{country.flag}</div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-bold text-lg text-foreground truncate">{country.name}</h3>
            
            {/* HOT Badge */}
            {isHot && (
              <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500 text-white text-xs font-medium">
                <Flame className="w-3 h-3" />
                HOT
              </span>
            )}
          </div>
          
          <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground">
            <span className="flex items-center gap-1">
              <MapPin className="w-3.5 h-3.5" />
              {country.cityCount > 0 ? `${country.cityCount} ${country.cityCount === 1 ? 'City' : 'Cities'}` : 'All cities'}
            </span>
            <span>•</span>
            <span>{country.boothCount} {country.boothCount === 1 ? 'Booth' : 'Booths'}</span>
          </div>
        </div>

        {/* Favorite button */}
        {onToggleFavorite && (
          <FavoriteButton
            isFavorite={isFavorite}
            onClick={(e) => {
              e.stopPropagation();
              onToggleFavorite();
            }}
            size="sm"
          />
        )}

        {/* Live indicator & arrow */}
        <div className="flex items-center gap-3">
          {isActive && country.participantCount > 0 && (
            <div className="flex items-center gap-1.5">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" />
              </span>
              <span className="text-sm font-semibold text-green-600">{country.participantCount}</span>
            </div>
          )}
          <ChevronRight className="w-5 h-5 text-muted-foreground" />
        </div>
      </div>
    </button>
  );
};

export default ExhibitionCountryCard;
